<template>
  <section class="section section-observed news-publish-page">
    <!-- 总览：图二式 2×2 分栏 -->
    <div v-if="!activeBoard" class="publish-overview">
      <div v-if="overviewLoading" class="panel-state muted-copy">正在加载...</div>
      <div v-else class="publish-grid">
        <article
          v-for="board in boards"
          :key="board.code"
          class="publish-block"
        >
          <header class="publish-block__head">
            <div class="publish-block__title">
              <span class="publish-block__icon" aria-hidden="true">
                <svg v-if="board.code === 'meeting_notice'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                  <rect x="3" y="5" width="18" height="16" rx="2" />
                  <path d="M8 3v4M16 3v4M3 10h18" />
                </svg>
                <svg v-else-if="board.code === 'important_news'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                  <path d="M4 5h12v14H4z" />
                  <path d="M16 8h4v11h-4" />
                  <path d="M7 9h6M7 13h6M7 17h4" />
                </svg>
                <svg v-else-if="board.code === 'society_dynamics'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                  <path d="M12 3l2.2 4.5L19 8.2l-3.5 3.4.8 4.9L12 14.2 7.7 16.5l.8-4.9L5 8.2l4.8-.7L12 3z" />
                </svg>
                <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                  <path d="M4 19V5M4 19h16M8 15V9M12 15V7M16 15v-4" />
                </svg>
              </span>
              <h3>{{ board.label }}</h3>
            </div>
            <button type="button" class="text-link" @click="openBoard(board.code)">更多 &gt;&gt;</button>
          </header>
          <ul class="publish-block__list">
            <li v-for="item in (overviewItems[board.code] || [])" :key="itemKey(item)">
              <button type="button" class="publish-block__item" @click="openItem(item)">
                <i class="publish-block__dot" aria-hidden="true" />
                <span class="publish-block__item-title">{{ item.title }}</span>
                <time v-if="item.date">{{ item.date }}</time>
              </button>
            </li>
            <li v-if="!(overviewItems[board.code] || []).length" class="publish-block__empty muted-copy">
              暂无内容
            </li>
          </ul>
        </article>
      </div>
    </div>

    <!-- 单栏目列表（点击「更多 >>」进入）；回总览走面包屑「通知要闻」 -->
    <div v-else class="publish-board-view">
      <div class="content-panel">
        <header class="board-banner" :class="{ 'has-cover': !!activeCover }" :style="bannerStyle">
          <div class="board-banner-inner">
            <div class="board-title-row">
              <p>{{ activeBoardMeta.desc }}</p>
              <span class="board-count">共 {{ total }} 条</span>
            </div>
          </div>
        </header>

        <div v-if="loading" class="panel-state muted-copy">正在加载...</div>
        <div v-else-if="!filteredItems.length" class="panel-state empty-panel">
          <p class="muted-copy">暂无已发布的{{ activeBoardMeta.label }}内容</p>
        </div>
        <div v-else class="publish-list">
          <article
            v-for="item in filteredItems"
            :key="item.entryId || item.title"
            class="publish-card"
          >
            <button type="button" class="publish-card-main" @click="openItem(item)">
              <div class="publish-card-meta">
                <span>{{ activeBoardMeta.label }}</span>
                <time>{{ item.date || '待发布' }}</time>
                <span v-if="item.attachments && item.attachments.length" class="file-tag">
                  附件{{ item.attachments.length > 1 ? `×${item.attachments.length}` : '' }}
                </span>
              </div>
              <strong>{{ item.title }}</strong>
              <small>{{ item.summary || '暂无摘要' }}</small>
            </button>
            <button
              v-if="primaryAttachment(item)"
              type="button"
              class="ghost-action compact file-download"
              @click.stop="front.downloadEntryAttachment(item, primaryAttachment(item))"
            >
              下载附件
            </button>
          </article>
        </div>

        <FrontPagination
          :page-num="pageNum"
          :page-size="pageSize"
          :total="total"
          @change="onPageChange"
        />
      </div>
    </div>
  </section>
</template>

<script>
import { listPublicCmsEntries, unwrapPublicPage } from '../../api/paleo'
import FrontPagination from '../../components/common/FrontPagination.vue'
import { getAttachments, getBoardCovers, getOriginalFile } from '../../utils/cmsExtra'
import { PUBLISH_NAV_BOARDS } from '../../data/navMenus'

const OVERVIEW_SIZE = 5

const BOARD_DESC = {
  meeting_notice: '学术会议相关通知公告，包括会议征文、报名信息等。',
  important_news: '学会重点新闻与重要公告，及时传递权威信息。',
  society_dynamics: '学会活动、人才计划与综合动态，对应原站「新闻动态 / 学会动态」。',
  work_progress: '学会工作进展与公示信息，对应原站「新闻动态 / 工作进展」。'
}

const BOARDS = PUBLISH_NAV_BOARDS.map(board => ({
  ...board,
  desc: BOARD_DESC[board.code] || ''
}))

export default {
  name: 'NewsPublishPage',
  components: { FrontPagination },
  inject: ['front'],
  data() {
    return {
      boards: BOARDS,
      activeBoard: '',
      loading: false,
      overviewLoading: false,
      remoteItems: [],
      remoteCovers: null,
      overviewItems: {
        meeting_notice: [],
        important_news: [],
        society_dynamics: [],
        work_progress: []
      },
      pageNum: 1,
      pageSize: 5,
      total: 0,
      loadSeq: 0
    }
  },
  computed: {
    boardCovers() {
      if (this.remoteCovers) {
        return this.remoteCovers
      }
      return this.front.publishBoardCovers || {
        meeting_notice: '',
        important_news: '',
        society_dynamics: '',
        work_progress: ''
      }
    },
    activeBoardMeta() {
      return this.boards.find(item => item.code === this.activeBoard) || this.boards[0]
    },
    activeCover() {
      return this.boardCovers[this.activeBoard] || ''
    },
    coverDisplayUrl() {
      if (!this.activeCover) {
        return ''
      }
      return this.front.resolvePublicDisplayUrl
        ? this.front.resolvePublicDisplayUrl(this.activeCover)
        : this.front.resolveFileUrl(this.activeCover)
    },
    bannerStyle() {
      if (!this.coverDisplayUrl) {
        return {}
      }
      return {
        backgroundImage: `linear-gradient(100deg, rgba(0, 43, 73, 0.92) 0%, rgba(0, 43, 73, 0.72) 42%, rgba(0, 43, 73, 0.35) 100%), url("${this.coverDisplayUrl}")`
      }
    },
    filteredItems() {
      return this.remoteItems.map(item => {
        const mapped = this.front.mapCmsEntry(item)
        return {
          ...mapped,
          attachments: getAttachments(mapped),
          originalFile: getOriginalFile(mapped)
        }
      })
    }
  },
  watch: {
    '$route.query.board': {
      immediate: true,
      handler(code) {
        const allowed = this.boards.map(item => item.code)
        if (!code) {
          this.activeBoard = ''
          this.loadOverview()
          return
        }
        if (!allowed.includes(code)) {
          this.$router.replace({ path: '/news-publish' }).catch(() => {})
          return
        }
        this.activeBoard = code
        this.pageNum = 1
        this.loadPublish()
      }
    }
  },
  methods: {
    itemKey(item) {
      return item.entryId || item.contentId || item.title
    },
    openBoard(code) {
      this.$router.push({ path: '/news-publish', query: { board: code } }).catch(() => {})
    },
    onPageChange(page) {
      this.pageNum = page
      this.loadPublish()
    },
    primaryAttachment(item) {
      const list = (item && item.attachments) || []
      return list.find(file => file && file.url) || null
    },
    openItem(item) {
      if (item.entryId) {
        this.front.goPath(`/content/${item.entryId}`)
        return
      }
      this.front.openContentDetail(this.front.mapCmsEntryToContent(item))
    },
    async loadOverview() {
      this.overviewLoading = true
      try {
        const results = await Promise.all(
          this.boards.map(board =>
            listPublicCmsEntries({
              moduleCode: 'publish',
              columnCode: board.code,
              pageNum: 1,
              pageSize: OVERVIEW_SIZE
            }).catch(() => ({ rows: [], total: 0 }))
          )
        )
        const next = { ...this.overviewItems }
        this.boards.forEach((board, index) => {
          const rows = unwrapPublicPage(results[index]).rows || []
          next[board.code] = rows.map(row => this.front.mapCmsEntry(row))
        })
        this.overviewItems = next
      } catch (error) {
        // 总览失败保持空态
      } finally {
        this.overviewLoading = false
      }
    },
    async loadPublish() {
      if (!this.activeBoard) return
      this.loading = true
      const seq = ++this.loadSeq
      try {
        const [publishResult, settingsResult] = await Promise.all([
          listPublicCmsEntries({
            moduleCode: 'publish',
            columnCode: this.activeBoard,
            pageNum: this.pageNum,
            pageSize: this.pageSize
          }),
          listPublicCmsEntries({ moduleCode: 'settings', pageNum: 1, pageSize: 50 }).catch(() => ({ rows: [], total: 0 }))
        ])
        if (seq !== this.loadSeq) return
        const page = unwrapPublicPage(publishResult, {
          pageNum: this.pageNum,
          pageSize: this.pageSize
        })
        this.remoteItems = page.rows
        this.total = page.total
        const settings = unwrapPublicPage(settingsResult).rows
        const coversEntry = settings.find(item => item.columnCode === 'publish_board_covers')
          || settings.find(item => item.columnCode === 'site_config')
        if (coversEntry) {
          this.remoteCovers = getBoardCovers(coversEntry)
        }
      } catch (error) {
        if (seq !== this.loadSeq) return
        this.remoteItems = []
        this.total = 0
      } finally {
        if (seq === this.loadSeq) this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.news-publish-page {
  max-width: var(--content-max);
}

.publish-overview {
  min-width: 0;
}

.publish-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 28px 40px;
}

.publish-block {
  min-width: 0;
  border-top: 3px solid var(--primary);
  background: #fff;
  padding: 14px 4px 8px;
}

.publish-block__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
  padding: 0 4px 8px;
  border-bottom: 1px solid rgba(0, 43, 73, 0.08);
}

.publish-block__title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.publish-block__icon {
  display: inline-grid;
  width: 22px;
  height: 22px;
  place-items: center;
  color: var(--primary);
  flex-shrink: 0;
}

.publish-block__icon svg {
  width: 20px;
  height: 20px;
}

.publish-block__title h3 {
  margin: 0;
  color: #1a1a1a;
  font-size: 18px;
  font-weight: 800;
  letter-spacing: 0.02em;
}

.publish-block__head .text-link {
  border: 0;
  background: transparent;
  color: var(--subtle, #6b7280);
  font-size: 13px;
  cursor: pointer;
  padding: 0;
  white-space: nowrap;
}

.publish-block__head .text-link:hover {
  color: var(--primary);
  text-decoration: underline;
}

.publish-block__list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.publish-block__item {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 8px;
  align-items: baseline;
  width: 100%;
  border: 0;
  padding: 9px 4px;
  background: transparent;
  color: inherit;
  text-align: left;
  cursor: pointer;
  font: inherit;
}

.publish-block__dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #9aa3ad;
  flex-shrink: 0;
  margin-top: 0.45em;
}

.publish-block__item-title {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #1f2937;
  font-size: 14px;
  line-height: 1.5;
}

.publish-block__item:hover .publish-block__item-title {
  color: #c41e3a;
}

.publish-block__item time {
  flex-shrink: 0;
  color: #8a93a0;
  font-size: 13px;
  white-space: nowrap;
}

.publish-block__empty {
  padding: 12px 4px;
  margin: 0;
  font-size: 13px;
}

.content-panel {
  border: 1px solid var(--border);
  border-top: 3px solid var(--primary);
  border-radius: var(--front-radius);
  background: #fff;
  box-shadow: var(--shadow-soft, 0 8px 24px rgba(0, 43, 73, 0.06));
  overflow: hidden;
}

.board-banner {
  position: relative;
  display: flex;
  align-items: flex-end;
  min-height: 148px;
  padding: 22px 20px;
  border-bottom: 1px solid var(--border);
  background-color: var(--primary);
  background-size: cover;
  background-position: center;
  color: #fff;
}

.board-banner-inner {
  position: relative;
  z-index: 1;
  width: 100%;
  min-width: 0;
  display: grid;
  gap: 8px;
}

.board-title-row {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.board-title-row p {
  margin: 0;
  flex: 1 1 12em;
  max-width: 38em;
  color: rgba(255, 255, 255, 0.86);
  font-size: 13px;
  line-height: 1.6;
}

.board-banner-inner h3 {
  margin: 0;
  color: #fff;
  font-size: 22px;
  line-height: 1.3;
}

.board-banner-inner > p {
  margin: 0;
  max-width: 38em;
  color: rgba(255, 255, 255, 0.86);
  font-size: 13px;
  line-height: 1.6;
}

.board-count {
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  padding: 0 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.16);
  color: #fff;
  font-size: 12px;
  font-weight: 800;
  white-space: nowrap;
}

.panel-state {
  padding: 28px 16px;
}

.publish-list {
  display: grid;
  gap: 0;
}

.publish-card {
  display: grid;
  gap: 10px;
  padding: 16px;
  border: 0;
  border-bottom: 1px solid var(--border);
  background: #fff;
}

.publish-card:last-child {
  border-bottom: 0;
}

.publish-card:hover {
  background: rgba(0, 43, 73, 0.02);
}

.publish-card-main {
  display: grid;
  gap: 8px;
  width: 100%;
  border: 0;
  padding: 0;
  background: transparent;
  color: var(--text);
  text-align: left;
  cursor: pointer;
}

.publish-card-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  color: var(--subtle);
  font-size: 12px;
}

.publish-card-meta span:first-child {
  display: inline-flex;
  min-height: 22px;
  align-items: center;
  padding: 0 8px;
  border-radius: 999px;
  background: rgba(0, 43, 73, 0.08);
  color: var(--primary);
  font-weight: 800;
}

.file-tag {
  display: inline-flex;
  min-height: 22px;
  align-items: center;
  padding: 0 8px;
  border-radius: 999px;
  border: 1px solid #c8e6c9;
  background: #e8f5e9;
  color: #2e7d32;
  font-weight: 700;
}

.publish-card strong {
  color: var(--primary);
  font-size: 16px;
  line-height: 1.4;
}

.publish-card small {
  color: var(--subtle);
  font-size: 13px;
  line-height: 1.6;
}

.file-download {
  justify-self: start;
  text-decoration: none;
}

.empty-panel {
  text-align: center;
}

@media (max-width: 860px) {
  .publish-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .board-banner {
    min-height: 128px;
    padding: 18px 16px;
  }

  .board-banner-inner h3 {
    font-size: 18px;
  }
}
</style>
